/**
 * Notification API 전용 custom axios instance
 */
import { createOrvalCustomInstance } from '@edusense/shared-auth-core';

const runtimeEnvOrder = [
  'VITE_NOTIFICATION_API_BASE_URL',
  'NOTIFICATION_API_BASE_URL',
  'VITE_NOTIFICATION_FALLBACK_BASE_URL',
  'NOTIFICATION_FALLBACK_BASE_URL',
  'VITE_API_BASE_URL',
];

const streamPathKeys = [
  'VITE_NOTIFICATION_FALLBACK_STREAM_PATH',
  'NOTIFICATION_FALLBACK_STREAM_PATH',
];

const listPathKeys = [
  'VITE_NOTIFICATION_FALLBACK_LIST_PATH',
  'NOTIFICATION_FALLBACK_LIST_PATH',
];

const apiKeyKeys = ['VITE_NOTIFICATION_API_KEY', 'NOTIFICATION_API_KEY'];

const readEnv = (key: string): string | undefined => {
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as any).env) {
      const value = (import.meta as any).env[key];
      if (typeof value === 'string' && value.length > 0) {
        return value;
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.debug('[notification-client] import.meta env read failed', error);
  }

  if (typeof window !== 'undefined') {
    const win = window as typeof window & {
      env?: Record<string, string>;
      __APP_CONFIG__?: Record<string, string>;
    };
    const runtime = win.env?.[key];
    if (runtime) {
      return runtime;
    }
    const appConfig = win.__APP_CONFIG__?.[key];
    if (appConfig) {
      return appConfig;
    }
  }

  if (typeof process !== 'undefined' && process.env) {
    const candidates = [process.env[key], process.env[`NEXT_PUBLIC_${key}`]];
    for (const candidate of candidates) {
      if (candidate) {
        return candidate;
      }
    }
  }

  return undefined;
};

const resolveValue = (keys: string[], fallback?: string): string | undefined => {
  for (const key of keys) {
    const value = readEnv(key);
    if (value) {
      return value;
    }
  }
  return fallback;
};

export const resolveNotificationApiBaseUrl = (): string => {
  return resolveValue(runtimeEnvOrder, 'https://api.ugot.uk')!;
};

export const resolveNotificationListPath = (): string => {
  return resolveValue(listPathKeys, '/api/notifications')!;
};

export const resolveNotificationStreamPath = (): string => {
  return resolveValue(streamPathKeys, '/api/notifications/stream')!;
};

export const resolveNotificationApiKey = (): string | undefined => {
  return resolveValue(apiKeyKeys);
};

export const customInstance = createOrvalCustomInstance(resolveNotificationApiBaseUrl(), {
  timeout: 30000,
  defaultHeaders: (() => {
    const apiKey = resolveNotificationApiKey();
    return {
      Accept: 'application/json',
      ...(apiKey ? { 'X-Notification-Api-Key': apiKey } : {}),
    };
  })(),
  enableDebug: process.env.NODE_ENV === 'development',
  onAuthError: () => {
    console.warn('[notification-client] 인증이 만료되었습니다. 토큰만 정리합니다.');
  },
});

export type ErrorType<T> = import('@edusense/shared-auth-core').AuthAxiosError<T>;
